"use client";

import { useMemo } from "react";

import type { TaDeskCapability } from "@sdk/access";
import {
  canAccessBrand,
  hasAnyCapability,
  hasCapability,
} from "@sdk/access/resolve-access-context";

import { useAccessOrEmpty } from "./use-access";

export function useHasCapability(capability: TaDeskCapability): boolean {
  const ctx = useAccessOrEmpty();
  return useMemo(
    () => hasCapability(ctx, capability),
    [capability, ctx]
  );
}

export function useHasAnyCapability(capabilities: TaDeskCapability[]): boolean {
  const ctx = useAccessOrEmpty();
  return useMemo(
    () => hasAnyCapability(ctx, capabilities),
    [capabilities, ctx]
  );
}

/** Strict brand assignment check (not CRM read/view accounting scope). */
export function useHasBrandAssignment(
  brandId: string | undefined | null
): boolean {
  const ctx = useAccessOrEmpty();
  return useMemo(() => {
    if (!brandId) return true;
    return canAccessBrand(ctx, brandId);
  }, [brandId, ctx]);
}

export function useCanManageWorkspace(): boolean {
  const ctx = useAccessOrEmpty();
  return useMemo(
    () => ctx.isOwner || hasCapability(ctx, "ADMIN_ACCOUNT"),
    [ctx]
  );
}

export function useHasManageBookings(): boolean {
  return useHasCapability("MANAGE_BOOKINGS");
}

export function useHasViewAccounting(): boolean {
  return useHasCapability("VIEW_ACCOUNTING");
}

export function useHasManageAccounting(): boolean {
  return useHasCapability("MANAGE_ACCOUNTING");
}

export function useHasManageBookingFinance(): boolean {
  return useHasCapability("MANAGE_BOOKING_FINANCE");
}
